/**
 * welcome.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { AlgorithmService } from "app/algorithms/management/algorithm.service";
import { Algorithm } from "app/algorithms/structure";
import { SubjectService } from "app/subjects/subject.service";
import { AuthService } from "../auth/auth.service";
import { PasswordCredentials } from "../auth/identity/credentials";
import { LoginService } from "../auth/login.service";
import { AlertService } from "./alert.service";
import { BrandingService } from "./branding.service";

/**
 * The {@link WelcomeComponent} displays the front page of the application, with
 * a general description of its functionality and the user's status and options.
 */
@Component({
  template: `
  <div class="inset-background welcome-mat">
    <div class="container">
      <table class="splash center-block">
      <tr>
          <td rowspan="2">
            <img src="zone/images/splash_logo.png" class="logo">
          </td>
          <td class="title-box">
            <h4 class="pretitle">{{branding.strings.splash_pretitle}}</h4>
            <h1 class="title">
              {{branding.strings.splash_title}}
              <span
                *ngIf="branding.flags.has('use_title_trademark')"
                class="title-tm">
                ™️
              </span>
            </h1>
          </td>
        </tr>
        <tr>
          <td class="explanation">
            <p>
              {{branding.strings.splash_tagline}}
            </p>
          </td>
        </tr>
      </table>
    </div>
  </div>
  <!-- Your free interactive guide to the confusing world of health care benefits coverage. -->

  <!--<p>
    This site provides recommendations based on information you provide. You can answer as much or as little as you prefer, however the tool can make better suggestions if you fill it out accurately and completely. (We do not collect your name, phone, address, or other identifying information unless you specifically request additional help.)
  </p>
  <p>
    This application runs in your web browser over the internet, with no download required. If your connection is interrupted you may have to start over. To get started, simply click below!
  </p> -->
  <div class="container loginForm">
    <form [formGroup]="loginForm" *ngIf="!auth.authenticated">
      <div class="row">
        <div class="col-sm-3 col-sm-offset-2 form-group">
          <label>Username:</label>
          <input
            autofocus
      type="text"
      autocomplete="username"
            class="form-control"
            formControlName="username">
        </div>
        <div class="col-sm-3 form-group">
          <label>Password:</label>
          <input
      type="password"
      autocomplete="current-password"
            class="form-control"
            formControlName="password">
        </div>
        <div class="col-sm-2 form-group">
          <button
            class="btn btn-primary form-control align-with-labels"
            (click)="attemptLogIn()"
            [disabled]="!login.ready">
            Login
          </button>
        </div>
      </div>
      <div class="text-center">
        <a class="text-warning" routerLink="/forgot-password">
          Reset your password
        </a>
      </div>
    </form>
    <div class="text-center" *ngIf="auth.authenticated">
      <h3 class="info row text-center">
        You are already logged in!
      </h3>
      <a class="btn btn-success" routerLink="/home"><span class="glyphicon glyphicon-home"></span> Go to Your Home Page</a>
    </div>
  </div>

  <div class="inset-background">
    <div class="container">
      <table class="grant-info center-block">
        <tr>
          <td *ngIf="!branding.strings.splash_footer">
            <p>
              Advocus is a product of My Coverage Plan,
              a subsidiary of ABC for Health, Inc.
            </p>
            <p>
              My Coverage Plan was made possible in part by grant number
              <cite>5R43MD005810-02</cite> from the National Institute on
              Minority Health and Health Disparities.
            </p>
          </td>
          <td *ngIf="branding.strings.splash_footer">
            <p>
              {{branding.strings.splash_footer}}
            </p>
            <p>
              {{branding.strings.splash_footer_continued}}
            </p>
          </td>
          <td>
            <img src="zone/images/mcp_logo.png" class="logo mcp">
          </td>
          <td>
            <img src="zone/images/abc_logo.png" class="logo abc">
          </td>
        </tr>
      </table>
    </div>
  </div>
  `,
  styles: [``]
})
export class WelcomeComponent
{

  public loginUsername : FormControl = new FormControl();
  public loginPassword : FormControl = new FormControl();
  public loginForm : FormGroup = new FormGroup({
    username: this.loginUsername,
    password: this.loginPassword
  });

  /**
   * Construct a new {@link WelcomeComponent}.
   *
   */
  public constructor (
    protected auth : AuthService,
    protected login : LoginService,
    protected alerts : AlertService,
    public branding : BrandingService,
    public subjects : SubjectService,
    public algorithms : AlgorithmService
  ) {}

  /**
   * Use the {@link AuthService} to attempt a login, and redirect if the attempt
   * was successful.
   *
   * @return {boolean} Always false, to prevent HTML form submit navigation.
   */
  public attemptLogIn () : boolean
  {
    const credentials : PasswordCredentials =
      new PasswordCredentials(this.auth.hasher, this.auth.salter);
    credentials.username = this.loginUsername.value;
    credentials.password = this.loginPassword.value;

    try
    {
      // TODO: presubmit validation?
      this.login.attempt(credentials).subscribe(
        (authenticated : boolean) => {
          if (!authenticated) {
            this.alerts.warnAndReplace(
              "Your username and/or password are incorrect. Please try again.",
              "credential failure");
          }
          else
          {
            // this.alerts.notify(
            //   "Congratulations, you are now logged in!");
          }
        },
        (error) => console.error(error),
        () => this.auth.redirectIfAuthed()
      );
    }
    catch (e)
    {
      // ok hopefully this doesn't happen anymore
      this.alerts.error(
        "Could not log in -- connection has closed. (You may need to reload.)");
      console.error(e);
    }

    // Since this is hooked into the form's onSubmit, returning false prevents
    // the page from reloading
    return false;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/welcome.component.ts